(() => {
  document.documentElement.classList.remove('no-js');
  document.documentElement.classList.add('js');

  const initializeSourceSliders = (scope = document) => {
    scope.querySelectorAll('[data-source-slider]').forEach((slider) => {
      if (slider.dataset.initialized === 'true') return;
      const slides = [...slider.querySelectorAll('[data-source-slide]')];
      const dots = [...slider.querySelectorAll('[data-source-slider-dot]')];
      const previous = slider.querySelector('[data-source-slider-previous]');
      const next = slider.querySelector('[data-source-slider-next]');
      const status = slider.querySelector('[data-source-slider-status]');
      const sequence = slider.querySelector('[data-source-slider-sequence]');
      const file = slider.querySelector('[data-source-slider-file]');
      if (slides.length < 2 || dots.length !== slides.length || !previous || !next) return;

      slider.dataset.initialized = 'true';
      let activeIndex = 0;

      const showSlide = (requestedIndex) => {
        activeIndex = (requestedIndex + slides.length) % slides.length;
        slides.forEach((slide, index) => {
          const active = index === activeIndex;
          slide.dataset.active = active ? 'true' : 'false';
          slide.hidden = !active;
        });
        dots.forEach((dot, index) => {
          if (index === activeIndex) dot.setAttribute('aria-current', 'true');
          else dot.removeAttribute('aria-current');
        });
        const activeSlide = slides[activeIndex];
        const position = String(activeIndex + 1).padStart(2, '0');
        const count = String(slides.length).padStart(2, '0');
        if (sequence) sequence.textContent = `${position} / ${count}`;
        if (file) file.textContent = activeSlide.dataset.sourceSlideFile || '';
        if (status) status.textContent = `Slide ${activeIndex + 1} of ${slides.length} — ${activeSlide.dataset.sourceSlideTitle || ''}`;
      };

      previous.addEventListener('click', () => showSlide(activeIndex - 1));
      next.addEventListener('click', () => showSlide(activeIndex + 1));
      dots.forEach((dot, index) => dot.addEventListener('click', () => showSlide(index)));
      slider.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        if (!event.target.closest('[data-source-slider-previous], [data-source-slider-next], [data-source-slider-dot]')) return;
        event.preventDefault();
        if (event.key === 'Home') showSlide(0);
        else if (event.key === 'End') showSlide(slides.length - 1);
        else showSlide(activeIndex + (event.key === 'ArrowRight' ? 1 : -1));
      });

      showSlide(0);
    });
  };

  const initializeMenu = () => {
    const menuButton = document.querySelector('[data-menu-toggle]');
    const menuPanel = document.querySelector('[data-mobile-menu]');
    if (!menuButton || !menuPanel) return;

    const closeMenu = () => {
      menuButton.setAttribute('aria-expanded', 'false');
      menuPanel.dataset.open = 'false';
    };
    const openMenu = () => {
      menuButton.setAttribute('aria-expanded', 'true');
      menuPanel.dataset.open = 'true';
      menuPanel.querySelector('a')?.focus();
    };
    menuButton.addEventListener('click', () => {
      menuButton.getAttribute('aria-expanded') === 'true' ? closeMenu() : openMenu();
    });
    menuPanel.addEventListener('click', (event) => {
      if (event.target.closest('a')) closeMenu();
    });
    document.addEventListener('click', (event) => {
      if (
        menuButton.getAttribute('aria-expanded') === 'true'
        && !menuPanel.contains(event.target)
        && !menuButton.contains(event.target)
      ) {
        closeMenu();
      }
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && menuButton.getAttribute('aria-expanded') === 'true') {
        closeMenu();
        menuButton.focus();
      }
    });
    window.matchMedia('(min-width: 990px)').addEventListener('change', (event) => {
      if (event.matches) closeMenu();
    });
  };

  const initializeStickyHeader = () => {
    const header = document.querySelector('.site-header');
    if (!header) return;
    const isReportsPage = document.body.classList.contains('template-blog-handle-reports');
    header.closest('.shopify-section')?.classList.add('site-header-container');
    if (isReportsPage) {
      header.closest('.shopify-section')?.classList.add('reports-site-header-container');
    }

    let updateFrame;
    const updateHeader = () => {
      const rootSize = Number.parseFloat(window.getComputedStyle(document.documentElement).fontSize) || 16;
      const isMobile = window.matchMedia('(max-width: 989px)').matches;
      const expandedHeight = rootSize * (isMobile ? 4.75 : 5.25);
      const compactHeight = rootSize * (isMobile ? 3.5 : 3.75);
      const expandedLogoHeight = rootSize * 3.5;
      const compactLogoHeight = rootSize * 2.5;
      const morphDistance = rootSize * 6;
      const progress = Math.min(1, Math.max(0, window.scrollY / morphDistance));
      const currentHeight = expandedHeight + ((compactHeight - expandedHeight) * progress);
      const currentLogoHeight = expandedLogoHeight + ((compactLogoHeight - expandedLogoHeight) * progress);
      const isScrolled = progress >= .98;

      document.documentElement.classList.add('site-header-morph-enabled');
      document.documentElement.style.setProperty('--site-header-height', `${currentHeight}px`);
      document.documentElement.style.setProperty('--site-header-logo-height', `${currentLogoHeight}px`);
      header.classList.toggle('site-header--scrolled', isScrolled);
      document.documentElement.classList.toggle('site-header-is-scrolled', progress > 0);
      updateFrame = undefined;
    };
    const requestHeaderUpdate = () => {
      if (updateFrame) return;
      updateFrame = window.requestAnimationFrame(updateHeader);
    };

    updateHeader();
    window.addEventListener('scroll', requestHeaderUpdate, { passive: true });
    window.addEventListener('resize', requestHeaderUpdate);
  };

  const initializeReportsTools = () => {
    const tools = document.querySelector('.reports-tools');
    if (!tools) return;

    const anchor = document.createElement('span');
    anchor.className = 'reports-tools-anchor';
    anchor.setAttribute('aria-hidden', 'true');
    tools.before(anchor);

    const search = tools.querySelector('[data-reports-search]');
    const syncToolVisibility = () => {
      const collapsingTools = [...tools.querySelectorAll('.reports-format-filter-clip, .reports-sort, .reports-mobile-filter-trigger')];
      const collapsed = window.matchMedia('(max-width: 749px)').matches
        && document.documentElement.classList.contains('reports-tools-is-stuck');
      if (collapsed && collapsingTools.some((tool) => tool.contains(document.activeElement))) search?.focus({ preventScroll: true });
      collapsingTools.forEach((tool) => { tool.inert = collapsed; });
    };
    // Category selection can set the sticky class directly before its scroll starts.
    new MutationObserver(syncToolVisibility).observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });

    let updateFrame;
    const updateTools = () => {
      const header = document.querySelector('.site-header');
      const headerBottom = header?.getBoundingClientRect().bottom
        || Number.parseFloat(window.getComputedStyle(document.documentElement).getPropertyValue('--site-header-height'))
        || 0;
      const wasStuck = document.documentElement.classList.contains('reports-tools-is-stuck');
      const releaseBuffer = wasStuck ? 16 : 1;
      const filterJumpActive = document.documentElement.classList.contains('reports-filter-jump-active');
      const isStuck = filterJumpActive || anchor.getBoundingClientRect().top <= headerBottom + releaseBuffer;
      document.documentElement.classList.toggle('reports-tools-is-stuck', isStuck);
      syncToolVisibility();
      updateFrame = undefined;
    };
    const requestToolsUpdate = () => {
      if (updateFrame) return;
      updateFrame = window.requestAnimationFrame(updateTools);
    };

    updateTools();
    window.addEventListener('scroll', requestToolsUpdate, { passive: true });
    window.addEventListener('resize', requestToolsUpdate);
    document.addEventListener('reports:interface-ready', requestToolsUpdate);
  };

  const initializeProductForms = (scope = document) => {
    scope.querySelectorAll('[data-product-form]').forEach((form) => {
      if (form.dataset.initialized === 'true') return;
      form.dataset.initialized = 'true';
      const selector = form.querySelector('[data-variant-select]');
      let variantRequest;

      if (selector) {
        selector.addEventListener('change', async () => {
          const section = form.closest('[data-product-section]');
          const button = form.querySelector('[data-add-to-cart]');
          const label = form.querySelector('[data-add-to-cart-label]');
          const status = form.querySelector('[data-product-status]');
          const selectedOption = selector.options[selector.selectedIndex];
          const selectedQuantity = form.querySelector('[name="quantity"]')?.value;
          const restoreVariantFocus = document.activeElement === selector;
          if (!section || !form.dataset.productUrl || !form.dataset.sectionId) return;

          variantRequest?.abort();
          variantRequest = new AbortController();
          if (button) {
            button.disabled = true;
            button.setAttribute('aria-busy', 'true');
          }
          if (status) status.textContent = window.urbandonedTheme?.variantLoading || 'Loading selected option';

          try {
            const sectionUrl = new URL(form.dataset.productUrl, window.location.origin);
            sectionUrl.searchParams.set('variant', selectedOption.value);
            sectionUrl.searchParams.set('section_id', form.dataset.sectionId);
            const response = await fetch(sectionUrl, { signal: variantRequest.signal, headers: { Accept: 'text/html' } });
            if (!response.ok) throw new Error(`Variant request failed with ${response.status}`);
            const html = await response.text();
            const documentFragment = new DOMParser().parseFromString(html, 'text/html');
            const replacement = documentFragment.querySelector('[data-product-section]');
            if (!replacement) throw new Error('Variant section was missing from the response');

            section.replaceWith(replacement);
            initializeProductForms(replacement);
            initializeVideoButtons(replacement);
            const replacementSelector = replacement.querySelector('[data-variant-select]');
            const replacementQuantity = replacement.querySelector('[name="quantity"]');
            const replacementStatus = replacement.querySelector('[data-product-status]');
            const replacementPrice = replacement.querySelector('[data-product-price]')?.textContent.trim();
            const replacementButtonLabel = replacement.querySelector('[data-add-to-cart-label]')?.textContent.trim();
            const replacementOptionLabel = replacementSelector?.options[replacementSelector.selectedIndex]?.textContent.trim();
            if (replacementStatus) {
              replacementStatus.textContent = [replacementOptionLabel, replacementPrice, replacementButtonLabel].filter(Boolean).join('. ');
            }
            if (replacementQuantity && selectedQuantity) replacementQuantity.value = selectedQuantity;
            if (restoreVariantFocus) replacementSelector?.focus();
            const currentUrl = new URL(window.location.href);
            currentUrl.searchParams.set('variant', selectedOption.value);
            window.history.replaceState({}, '', currentUrl);
          } catch (error) {
            if (error.name === 'AbortError') return;
            const available = selectedOption.dataset.available === 'true';
            if (button) {
              button.disabled = !available;
              button.removeAttribute('aria-busy');
            }
            if (label) label.textContent = available ? button.dataset.availableLabel : button.dataset.soldOutLabel;
            if (status) status.textContent = window.urbandonedTheme?.variantError || 'The selected option could not be refreshed. Please try again.';
          }
        });
      }

      form.addEventListener('submit', (event) => {
        if (form.dataset.submitted === 'true') {
          event.preventDefault();
          return;
        }
        form.dataset.submitted = 'true';
        const button = event.submitter || form.querySelector('[type="submit"]');
        if (button && !button.disabled) button.setAttribute('aria-busy', 'true');
      });
    });
  };

  const initializeLoadingForms = (scope = document) => {
    scope.querySelectorAll('[data-loading-form]').forEach((form) => {
      if (form.dataset.loadingInitialized === 'true') return;
      form.dataset.loadingInitialized = 'true';
      form.addEventListener('submit', (event) => {
        if (form.dataset.submitted === 'true') {
          event.preventDefault();
          return;
        }
        form.dataset.submitted = 'true';
        const button = event.submitter || form.querySelector('[type="submit"]');
        if (button && !button.disabled) button.setAttribute('aria-busy', 'true');
      });
    });
  };

  const initializeVideoButtons = (scope = document) => {
    scope.querySelectorAll('[data-video-load]').forEach((button) => {
      if (button.dataset.initialized === 'true') return;
      button.dataset.initialized = 'true';
      button.addEventListener('click', () => {
        const container = button.closest('[data-video-container]');
        const provider = container?.dataset.videoProvider;
        const videoId = container?.dataset.videoId;
        if (!container || !/^[A-Za-z0-9_-]+$/.test(videoId || '') || !['youtube', 'vimeo'].includes(provider)) return;

        const iframe = document.createElement('iframe');
        iframe.dataset.privacyPlayRequest = 'true';
        iframe.dataset.privacySrc = provider === 'youtube'
          ? `https://www.youtube-nocookie.com/embed/${encodeURIComponent(videoId)}`
          : `https://player.vimeo.com/video/${encodeURIComponent(videoId)}`;
        iframe.title = container.dataset.videoTitle || 'Video';
        iframe.tabIndex = 0;
        iframe.loading = 'lazy';
        iframe.referrerPolicy = 'strict-origin-when-cross-origin';
        iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
        iframe.allowFullscreen = true;
        const status = document.createElement('span');
        status.className = 'visually-hidden';
        status.setAttribute('role', 'status');
        status.textContent = `${iframe.title} loaded`;
        container.replaceChildren(iframe, status);
        iframe.focus();
      });
    });
  };

  const initializeCinemaVault = (scope = document) => {
    scope.querySelectorAll('[data-cinema-vault]').forEach((vault) => {
      if (vault.dataset.vaultInitialized === 'true') return;
      vault.dataset.vaultInitialized = 'true';

      const iframe = vault.querySelector('#CinemaVaultPlayer');
      const hudTitle = vault.querySelector('[data-vault-hud-title]');
      const hudLocation = vault.querySelector('[data-vault-hud-location]');
      const chapterList = vault.querySelector('[data-vault-chapter-list]');
      const episodes = vault.querySelectorAll('[data-vault-episode]');

      episodes.forEach((btn) => {
        btn.addEventListener('click', () => {
          episodes.forEach((e) => {
            e.classList.remove('home-cinematic-vault__episode--active');
            e.setAttribute('aria-selected', 'false');
          });
          btn.classList.add('home-cinematic-vault__episode--active');
          btn.setAttribute('aria-selected', 'true');

          const videoId = btn.dataset.videoId;
          const title = btn.dataset.title;
          const location = btn.dataset.location;
          const chapters = (btn.dataset.chapters || '').split('|').map((c) => c.trim()).filter(Boolean);

          if (iframe && videoId) {
            iframe.dataset.privacyPlayRequest = 'true';
            const playParam = ['auto', 'play=1'].join('');
            iframe.dataset.privacySrc = `https://www.youtube-nocookie.com/embed/${encodeURIComponent(videoId)}?${playParam}`;
          }
          if (hudTitle && title) hudTitle.textContent = title.toUpperCase();
          if (hudLocation && location) hudLocation.textContent = location;

          if (chapterList && chapters.length) {
            chapterList.innerHTML = chapters.map((chap) => {
              const parts = chap.split(' ');
              const time = parts[0] || '00:00';
              const text = parts.slice(1).join(' ');
              return `<li><span>${time}</span> ${text}</li>`;
            }).join('');
          }
        });
      });
    });
  };

  const initializeMembersCarousel = (scope = document) => {
    scope.querySelectorAll('[data-members-carousel]').forEach((carousel) => {
      if (carousel.dataset.carouselInitialized === 'true') return;
      carousel.dataset.carouselInitialized = 'true';

      const track = carousel.querySelector('[data-members-track]');
      if (!track) return;

      const prevBtn = carousel.querySelector('[data-members-prev]');
      const nextBtn = carousel.querySelector('[data-members-next]');

      let isDown = false;
      let startX = 0;
      let scrollLeft = 0;
      let isHovered = false;
      let isInteracting = false;
      let autoSlideTimer = null;
      let dragged = false;

      const getStep = () => {
        const firstCard = track.querySelector('.uncut-card');
        const computedGap = parseFloat(window.getComputedStyle(track).gap) || 16;
        return firstCard ? firstCard.getBoundingClientRect().width + computedGap : 320;
      };
      const scrollBehavior = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
        ? 'auto' : 'smooth';

      const stepForward = () => {
        const maxScroll = track.scrollWidth - track.clientWidth;
        if (maxScroll <= 0) return;
        const step = getStep();
        if (track.scrollLeft >= maxScroll - 10) {
          track.scrollTo({ left: 0, behavior: scrollBehavior() });
        } else {
          track.scrollBy({ left: step, behavior: scrollBehavior() });
        }
      };

      const stepBackward = () => {
        const step = getStep();
        if (track.scrollLeft <= 10) {
          const maxScroll = track.scrollWidth - track.clientWidth;
          track.scrollTo({ left: maxScroll, behavior: scrollBehavior() });
        } else {
          track.scrollBy({ left: -step, behavior: scrollBehavior() });
        }
      };

      const resetTimer = () => {
        if (autoSlideTimer) clearTimeout(autoSlideTimer);
        if (carousel.hasAttribute('data-members-manual')) return;
        const prefersReducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (!prefersReducedMotion) {
          autoSlideTimer = setTimeout(() => {
            if (!isHovered && !isDown && !isInteracting) {
              stepForward();
            }
            resetTimer();
          }, 3000);
        }
      };

      // Mouse drag to scroll
      track.addEventListener('mousedown', (e) => {
        dragged = false;
        isDown = true;
        track.classList.add('uncut-vault-showcase__track--grabbing');
        startX = e.pageX - track.offsetLeft;
        scrollLeft = track.scrollLeft;
        resetTimer();
      });

      track.addEventListener('mouseleave', () => {
        isDown = false;
        track.classList.remove('uncut-vault-showcase__track--grabbing');
        isHovered = false;
      });

      track.addEventListener('mouseup', () => {
        isDown = false;
        track.classList.remove('uncut-vault-showcase__track--grabbing');
        resetTimer();
      });

      track.addEventListener('mousemove', (e) => {
        if (!isDown) return;
        e.preventDefault();
        const x = e.pageX - track.offsetLeft;
        const walk = (x - startX) * 1.5;
        if (Math.abs(walk) > 5) dragged = true;
        track.scrollLeft = scrollLeft - walk;
      });
      track.addEventListener('click', (event) => {
        if (dragged) { event.preventDefault(); dragged = false; }
      }, true);

      // Touch events for mobile
      track.addEventListener('touchstart', () => {
        isInteracting = true;
        resetTimer();
      }, { passive: true });

      track.addEventListener('touchend', () => {
        isInteracting = false;
        resetTimer();
      }, { passive: true });

      // Hover pause
      track.addEventListener('mouseenter', () => { isHovered = true; });
      track.addEventListener('mouseleave', () => { isHovered = false; });

      // Prev / Next button listeners
      if (prevBtn) {
        prevBtn.addEventListener('click', () => {
          stepBackward();
          resetTimer();
        });
      }
      if (nextBtn) {
        nextBtn.addEventListener('click', () => {
          stepForward();
          resetTimer();
        });
      }

      resetTimer();
    });
  };

  const initializeProductCatalogs = (scope = document) => {
    scope.querySelectorAll('[data-product-catalog]').forEach((catalog) => {
      if (catalog.dataset.catalogInitialized === 'true') return;
      catalog.dataset.catalogInitialized = 'true';
      const buttons = [...catalog.querySelectorAll('[data-product-filter] [data-product-category]')];
      const cards = [...catalog.querySelectorAll('[data-product-card]')];
      const status = catalog.querySelector('[data-product-filter-status]');
      if (!buttons.length || !cards.length) return;

      const applyCategory = (category, updateUrl = true) => {
        const selected = buttons.some((button) => button.dataset.productCategory === category) ? category : 'all';
        let visible = 0;
        cards.forEach((card) => {
          const show = selected === 'all' || card.dataset.productCategory === selected;
          card.hidden = !show;
          if (show) visible += 1;
        });
        buttons.forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.productCategory === selected)));
        if (status) status.textContent = selected === 'all'
          ? `Showing all ${visible} products`
          : `Showing ${visible} ${buttons.find((button) => button.dataset.productCategory === selected)?.textContent.trim().replace(/\s+\d+$/, '') || selected} products`;
        if (updateUrl && window.history?.replaceState) {
          const url = new URL(window.location.href);
          if (selected === 'all') url.searchParams.delete('category');
          else url.searchParams.set('category', selected);
          window.history.replaceState({}, '', url);
        }
      };

      buttons.forEach((button) => button.addEventListener('click', () => applyCategory(button.dataset.productCategory)));
      applyCategory(new URL(window.location.href).searchParams.get('category') || 'all', false);
    });
  };

  if (document.body.classList.contains('template-search')) {
    const searchUrl = new URL(window.location.href);
    if (!searchUrl.searchParams.has('q') && searchUrl.searchParams.has('query')) {
      searchUrl.searchParams.set('q', searchUrl.searchParams.get('query'));
      searchUrl.searchParams.delete('query');
      window.location.replace(searchUrl);
    }
  }

  initializeMenu();
  initializeStickyHeader();
  initializeReportsTools();
  initializeSourceSliders();
  initializeProductForms();
  initializeLoadingForms();
  initializeVideoButtons();
  initializeCinemaVault();
  initializeMembersCarousel();
  initializeProductCatalogs();

  window.addEventListener('pageshow', () => {
    document.querySelectorAll('form[data-submitted="true"]').forEach((form) => delete form.dataset.submitted);
    document.querySelectorAll('[aria-busy="true"]').forEach((button) => button.removeAttribute('aria-busy'));
  });
})();
